"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import {
  isProPriceExperimentEligible,
  PRO_PRICE_FLAG_KEY,
  ProPriceVariant,
  proPriceVariantFor,
} from "../helpers/proPriceExperiment";
import { useProfile } from "./useProfile";

const OVERRIDE_FEATURE_FLAGS_KEY = "$override_feature_flags";
const STORED_PERSON_PROPERTIES_KEY = "$stored_person_properties";

// The Pro price arm of the "pricing-pro-59" experiment (see
// helpers/proPriceExperiment.ts):
// - Only eligible profiles (non-guest, created on/after the launch date)
//   evaluate the flag. Everyone else gets control without touching it.
// - Exposure is the $feature_flag_called that getFeatureFlag records here.
//   Only PricingCards mounts this hook, so an exposed user is one who saw the
//   plan prices (paywall modal or /pricing).
// - The flag's release conditions match on signed_up_at / is_guest, fed via
//   setPersonPropertiesForFlags so evaluation never waits on person-property
//   ingestion (same values klap-api passes as personProperties).
// - No experience continuity: after identify() the web and klap-api hash the
//   same profile id, so the price shown is the price charged.
// Toolbar / console overrides win over the eligibility gate for local QA, but
// they only change the displayed price: klap-api charges what the real flag
// returns.
// Returns control until the profile and flags have loaded.
export const useProPriceVariant = (): ProPriceVariant => {
  const posthog = usePostHog();
  const { data: profile } = useProfile();
  const [flagVariant, setFlagVariant] = useState<
    string | boolean | undefined
  >();

  const isEligible = isProPriceExperimentEligible(profile);
  const signedUpAt =
    isEligible && profile?.createdAt
      ? new Date(profile.createdAt).toISOString()
      : undefined;

  // Guarded against re-sending identical values: setPersonPropertiesForFlags
  // (_, true) reloads flags, and useDefaultBillingInterval and
  // usePricingLimitVariant set the same values.
  useEffect(() => {
    if (!posthog || !signedUpAt) return;
    const stored = posthog.get_property?.(STORED_PERSON_PROPERTIES_KEY) as
      | Record<string, string>
      | undefined;
    if (stored?.signed_up_at === signedUpAt && stored?.is_guest === "false")
      return;
    posthog.setPersonPropertiesForFlags(
      { signed_up_at: signedUpAt, is_guest: "false" },
      true,
    );
  }, [posthog, signedUpAt]);

  useEffect(() => {
    if (!posthog) return;
    const update = () => {
      const overrides = posthog.get_property?.(OVERRIDE_FEATURE_FLAGS_KEY) as
        | Record<string, string | boolean>
        | undefined;
      const hasToolbarOverride =
        !!overrides &&
        typeof overrides === "object" &&
        Object.prototype.hasOwnProperty.call(overrides, PRO_PRICE_FLAG_KEY);
      setFlagVariant(
        hasToolbarOverride || isEligible
          ? posthog.getFeatureFlag(PRO_PRICE_FLAG_KEY)
          : undefined,
      );
    };
    update();
    return posthog.onFeatureFlags(update);
  }, [posthog, isEligible]);

  return proPriceVariantFor(flagVariant);
};
